import {FeedField, Field} from '@/api/models/Feeds.ts';
import {FeedKeySelect} from '@/pages/feeds/Editor/Mapper/FeedKeySelect.tsx';
import {MemoryFeedField} from '@/pages/feeds/Editor/Mapper/index.tsx';
import {SchemaFieldSelect} from '@/pages/feeds/Editor/Mapper/SchemaFieldSelect.tsx';
import {Button, Classes, Dialog, FormGroup} from '@blueprintjs/core';
import {ReactElement, useCallback, useMemo, useState} from 'react';

export type ColumnDef = [string, string];
export type CloseFn = () => void;
export type SaveFn = (field: MemoryFeedField) => void;

interface AddFieldDialogProps {
	keys: string[],
	sample: string[],
	onClose: CloseFn,
	onSave: SaveFn,
	field?: FeedField,
}

export function FieldDialog({field, onClose, onSave, keys, sample}: AddFieldDialogProps): ReactElement {
	const columns = useMemo<ColumnDef[]>(() => {
		const values = Object.values(sample);
		const columns: ColumnDef[] = [];

		for (let i = 0; i < keys.length; i++)
			columns.push([keys[i], values[i]]);

		return columns;
	}, [keys, sample]);

	const [feedKey, setFeedKey] = useState<string | null>(field?.feedKey ?? null);
	const onFeedKeySelect = useCallback((def: ColumnDef) => {
		setFeedKey(def[0]);
	}, []);

	const [schemaKey, setSchemaKey] = useState<Field | null>(field?.schemaKey ?? null);

	const onSaveClick = useCallback(() => {
		if (!feedKey || !schemaKey)
			return;

		const f: MemoryFeedField = field ? {...field} : {
			feedKey,
			schemaKey,
			mapper: null,
			fallbackValue: null,
		};

		onSave(f);
	}, [onSave, field, feedKey, schemaKey]);

	return (
		<Dialog isOpen={true} onClose={onClose} title={`${field ? 'Edit' : 'Add'} Field`}>
			<div className={Classes.DIALOG_BODY}>
				<FormGroup label="Feed Column">
					<FeedKeySelect items={columns} value={feedKey} onSelect={onFeedKeySelect} />
				</FormGroup>

				<FormGroup label="Schema Field">
					<SchemaFieldSelect value={schemaKey} onSelect={setSchemaKey} />
				</FormGroup>
			</div>

			<div className={Classes.DIALOG_FOOTER}>
				<div className={Classes.DIALOG_FOOTER_ACTIONS}>
					<Button text="Cancel" onClick={onClose} />
					<Button intent="primary" text="Save" onClick={onSaveClick} disabled={!feedKey || !schemaKey} />
				</div>
			</div>
		</Dialog>
	);
}
